// Angular and NGRX Imports
import { Injectable } from '@angular/core';
import { CanActivateChild } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';

// Classes
import { PriceAttribute, Prices, DiscountPrices } from 'src/app/ordering-platform/classes/PriceAttribute';
import { Product } from 'src/app/ordering-platform/classes/Product';
import { BaseProductAttributes, BaseProductAttributesValues } from 'src/app/ordering-platform/classes/BaseProductAttributes';
import { Address } from 'src/app/ordering-platform/classes/Address';
import { Line, lineDetails } from 'src/app/ordering-platform/classes/Line';
import { SofDisagree } from '../../classes/StatementOfFacts';
import { ProductDefaultLoi } from '../../classes/ProductDefaultLoi';
import { AcreagePopupMessage } from './../../classes/Order';

// Local imports
import { StatementsOfFactComponent } from '../../components/statements-of-fact/statements-of-fact.component';

/**
RULES FOR CONTAMINATED LAND
INVALID ATTRIBUTES - this takes presidence, if invalid attribute combination this overides bespoke and price.
BESPOKE - if the loi entered is less than the found LOI from the entered combination of attributes then the policy becomes bespoke.
-----------------------------------------------------------------------
FOUND PRICE - if the entered combination of attributes finds a price, it returns back with the price regardless of loi entered - for ex:
entered loi: £135, { Attribute combination: cp: 1, est: 2, FCI: 1 } returns price £190 - loi associated is £3,000,000 -
ignores loi entered as the attributes find the loi.
hence if entered loi is less than found loi then its bespoke.
*/


@Injectable({
    providedIn: 'root'
})

export class InsurancesService implements CanActivateChild {

    constructor() {
        // Reset all service data
        this.DisposeServiceData();
    }
    loi: string;
    productAddedFromList: boolean;
    BespokePolicyOnly: boolean;
    IsInsuranceComponent: boolean;
    getHardCopy: boolean;
    postOrder: boolean;
    addressSelectedString: string;
    popupText: string;
    BasketPriceTotal: number;
    enableContinue: boolean;
    CurrentDrafts: Line[];
    addToExistingCase: boolean;
    existingCaseId: number;
    existingOrderId: number;
    productNamePopup: string;
    lineGuid: string;
    draftCaseId: string;
    CurrentTaxTotal: number;
    SofValid: boolean;
    isBespoke: boolean;
    applyVoucher: boolean;
    voucherDiscount: number;
    selectedAddress: Address;

    policyFormValid = false;
    productAttributesFormValid = false;
    draftAttributesFormValid = false;
    showAttributePopup = false;
    isProdResidential = false;
    invalidAttrCombinationOnly = false;
    SofDisagreed = false;
    backToProductsClicked = false;

    // Array variables
    bespokeAttProducts: Product[];
    SOFBlocks: StatementsOfFactComponent[] = [];
    productDefaultLois: ProductDefaultLoi[] = [];
    popupProducts: any[];
    ProductList: Product[];
    Basket: Product[] = [];
    discountPrices: DiscountPrices[] = [];
    bespokeSOF: SofDisagree[] = [];
    PriceAttributes: PriceAttribute[] = [];
    CommonPriceAttributes: PriceAttribute[] = [];

    // Subject
    private priceAttributeSubject = new Subject<PriceAttribute[]>();
    PriceAttributeState = this.priceAttributeSubject.asObservable();

    private priceAttributeToRemoveSubject = new Subject<PriceAttribute[]>();
    PriceAttributeRemoveState = this.priceAttributeToRemoveSubject.asObservable();

    private policyAttributeToRemoveSubject = new Subject<BaseProductAttributes[]>();
    policyAttributeToRemoveState = this.policyAttributeToRemoveSubject.asObservable();

    private runPriceCheckSubject = new Subject<Product>();
    runPriceCheckState = this.runPriceCheckSubject.asObservable();

    private ProductSofRemoveSubject = new Subject<number>();
    productSofRemove = this.ProductSofRemoveSubject.asObservable();

    public rebuildPolicyDetailForm = new Subject<any>();
    rebuildPolicyDetailFormState = this.rebuildPolicyDetailForm.asObservable();

    private commonPriceAttributeSubject = new Subject<PriceAttribute[]>();
    CommonPriceAttributeState = this.commonPriceAttributeSubject.asObservable();

    // Being used in the app component - determines what insurance page to show
    private isInsuaranceSubject = new Subject<boolean>();
    isInsuranceState = this.isInsuaranceSubject.asObservable();

    private productSelectedSubject = new Subject<Product[]>();
    ProductState = this.productSelectedSubject.asObservable();
    // Current Insurance Basket - Products to be ordered.

    private isDraftPassedIn = new Subject<boolean>();
    draftPassedIn = this.isDraftPassedIn.asObservable();

    private isValidSubject = new Subject<boolean>();
    isValidState = this.isValidSubject.asObservable();

    // Bespoke and price
    private basketContainBespokeAndPrice = new Subject<boolean>();
    isBespokeState = this.basketContainBespokeAndPrice.asObservable();

    private policyFormSubject = new Subject<boolean>();
    policyAttributesValidState = this.policyFormSubject.asObservable();

    private draftAttributesValidSubject = new BehaviorSubject<boolean>(false);
    public draftAttributesValidState = this.draftAttributesValidSubject.asObservable();

    private productIsZero = new Subject<boolean>();
    productIsZeroState = this.productIsZero.asObservable();

    private bespokeAttributeAddSubject = new Subject<BaseProductAttributes>();
    bespokeAttributeAddState = this.bespokeAttributeAddSubject.asObservable();

    private bespokeAttributeRemoveSubject = new Subject<BaseProductAttributes>();
    bespokeAttributeRemoveState = this.bespokeAttributeRemoveSubject.asObservable();

    public showHardCopyModalSubject = new Subject<boolean>();
    showHardCopyModal = this.showHardCopyModalSubject.asObservable();

    public postAddressSubject = new Subject<boolean>();
    postAddress = this.postAddressSubject.asObservable()

    private applyVoucherSubject = new Subject<boolean>();
    applyVoucherState = this.applyVoucherSubject.asObservable();

    private voucherDiscountSubject = new Subject<number>();
    voucherDiscountState = this.voucherDiscountSubject.asObservable();

    private discountPricesSubject = new Subject<DiscountPrices[]>();
    discountPricesState = this.discountPricesSubject.asObservable();

    private bespokeSOFSubject = new Subject<SofDisagree[]>();
    bespokeSOFState = this.bespokeSOFSubject.asObservable();

    private selectedAddressSubject = new Subject<Address>();
    selectedAddressState = this.selectedAddressSubject.asObservable();

    private manualAddressSubject = new Subject<boolean>();
    manualAddressState = this.manualAddressSubject.asObservable();

    private removeDelProductControlsSubject = new Subject<Product>();
    removeControls = this.removeDelProductControlsSubject.asObservable();

    // BehaviorSubject
    private hardCopyCheckboxSubject = new BehaviorSubject<boolean>(false);
    hardCopyCheckbox$ = this.hardCopyCheckboxSubject.asObservable();

    private showPopupSubject = new BehaviorSubject<AcreagePopupMessage>({ productName: '', display: false });
    showPopup$ = this.showPopupSubject.asObservable();

    canActivateChild() {
        if (this.Basket.length > 0) {
            return true;
        }
        return false;
    }

    removeProductAttrs(product: Product) {
        this.removeDelProductControlsSubject.next(product);
    }

    RemoveProductSof(productId) {
        this.ProductSofRemoveSubject.next(productId);
    }

    setHardCopyCheckbox(value: boolean) {
        this.hardCopyCheckboxSubject.next(value);
    }

    togglePopup(productName: string, display: boolean) {
        this.showPopupSubject.next({ productName, display });
    }

    sendManualAddressEntered(res: any) {
        if (res === 'VALID') {
            this.manualAddressSubject.next(true);
        } else {
            this.manualAddressSubject.next(false);
        }
    }

    setDraftPassedIn(passedIn: boolean, lineGuid, caseId): any {
        this.lineGuid = lineGuid;
        this.draftCaseId = caseId;
        this.isDraftPassedIn.next(passedIn);
    }

    addCommonNonPriceAttribute(attribute: BaseProductAttributes, product: Product) {
        // Adding common attribute to the other products where they match
        this.Basket.forEach(prod => {
            const commonAttr = prod.nonPriceAttributes.find(x => x.name === attribute.name);
            if (commonAttr) {
                if (prod.commonNonPriceAttributs === undefined) {
                    prod.commonNonPriceAttributs = new Array<BaseProductAttributes>();
                }
                commonAttr.isCommonAttribute = true;

                if (!prod.commonNonPriceAttributs.find(x => x.name === commonAttr.name)) {
                    prod.commonNonPriceAttributs.push(commonAttr);
                }
            }
        });

        this.Basket.forEach(prod => {
            prod.nonPriceAttributes = prod.nonPriceAttributes.filter(attr => attr.name !== attribute.name);
        });
        attribute.isCommonAttribute = true;


        if (product.commonNonPriceAttributs === undefined) {
            product.commonNonPriceAttributs = new Array<BaseProductAttributes>();
        }
        if (!product.commonNonPriceAttributs.find(x => x.name === attribute.name)) {
            product.commonNonPriceAttributs.push(attribute);
        }
    }

    UnSubscribeProductState() {
        if (this.productSelectedSubject) {
            this.productSelectedSubject.unsubscribe();
        }
    }


    addCommonPriceAttribute(attribute: PriceAttribute, product: Product) {
        if (!this.CommonPriceAttributes) {
            this.CommonPriceAttributes = new Array<PriceAttribute>();
        }
        // Adding common attribute to the other products where they match
        this.Basket.forEach(prod => {
            const commonAttr = prod.priceAttributes.find(x => x.name === attribute.name);
            if (commonAttr) {
                if (prod.commonPriceAttributes === undefined) {
                    prod.commonPriceAttributes = new Array<PriceAttribute>();
                }
                commonAttr.isCommonAttribute = true;
                prod.commonPriceAttributes.push(commonAttr);
            }
        });

        this.PriceAttributes = this.PriceAttributes.filter((_item) => _item.name !== attribute.name
            && _item.productId !== attribute.productId);

        this.Basket.forEach(prod => {
            prod.priceAttributes = prod.priceAttributes.filter(attr => attr.name !== attribute.name);
        });


        if (product.commonPriceAttributes === undefined) {
            product.commonPriceAttributes = new Array<PriceAttribute>();
        }

        attribute.isCommonAttribute = true;
        product.commonPriceAttributes.push(attribute);

        if (this.CommonPriceAttributes.filter(x => x.name === attribute.name).length === 0) {
            this.CommonPriceAttributes.push(attribute);
        }
    }

    // attributes are new attri
    addPriceAttributes(attributes: PriceAttribute[]) {
        this.PriceAttributes = attributes;
        this.priceAttributeSubject.next(<PriceAttribute[]>attributes);
    }

    removeIndividualPriceAttribute(attribute: PriceAttribute) {
        this.PriceAttributes = this.PriceAttributes.filter((_item) => _item.name !== attribute.name
            && _item.productId !== attribute.productId);

        this.Basket.forEach(prod => {
            prod.priceAttributes = prod.priceAttributes.filter(attr => attr.name !== attribute.name);
        });

        this.priceAttributeSubject.next(<PriceAttribute[]>this.PriceAttributes);
    }

    removePriceAttributes(productId: number) {
        const foundProduct = this.Basket.find(x => x.productId === productId);
        // Checking if common attributes need to be removed and put back into specific
        if (foundProduct && foundProduct.commonPriceAttributes && foundProduct.commonPriceAttributes.length > 0) {
            this.ReShuffleCommonAttributes(foundProduct);
            this.Basket.forEach(prod => {
                this.addPriceAttributes(prod.priceAttributes);
            });
        } else {
            this.priceAttributeToRemoveSubject.next(foundProduct?.priceAttributes);
            this.PriceAttributes = this.PriceAttributes.filter((_item) => _item.productId !== productId);
            if (foundProduct) {
                foundProduct.priceAttributes = new Array<PriceAttribute>();
            }
        }
        // Remove Non Price attributes and reshuffle common and specific
        this.removeNonPriceAttributes(foundProduct);
    }

    removeNonPriceAttributes(foundProduct: Product) {

        // Goes to the policy detail component and removes specific attributes for that product
        this.policyAttributeToRemoveSubject.next(foundProduct?.nonPriceAttributes);
        if (foundProduct?.commonNonPriceAttributs && foundProduct?.commonNonPriceAttributs.length > 0) {
            this.ReShuffleNonPriceCommonAttributes(foundProduct);
        }
    }

    addProduct(product: Product) {
        const isDuplicate = this.Basket.find(x => x.productId === product.productId);

        if (!isDuplicate) {
            this.Basket.push(product);
        }
    }



    /**
     * sendBasketToChildComponents
     * Child components are subscribed to 'productSelectedSubject' waiting for basket
     * to be passed through - once it is it sets up the form controls
     * and the values.
     */
    sendBasketToChildComponents() {
        this.productSelectedSubject.next(<Product[]>this.Basket);
    }

    sendCommonPriceAttributes() {
        this.commonPriceAttributeSubject.next(this.CommonPriceAttributes);
    }

    removeProduct(product: Product) {
        this.Basket = this.Basket.filter((_item) => _item.productId !== product.productId);
        this.productSelectedSubject.next(<Product[]>this.Basket);
    }

    setProductIsBespoke(isBespoke: boolean, productId: number): any {
        const product = this.getProductById(productId);
        product.isBespokeProduct = isBespoke;
    }

    /**
    * CheckForBespokeOnly
    * Checks if order is just bespoke, and no prices
    */
    CheckIfAllProductsInOrderAreBespoke(products: Product[]): any {

        if (products === undefined) { return false; }
        if (products.length === 0) { return false; }
        const bespokeCount = products.filter(x => x.isBespokeProduct).length;
        if (bespokeCount === products.length) {
            // all
            this.BespokePolicyOnly = true;
            this.basketContainBespokeAndPrice.next(false);
            return true;
        } else {
            this.BespokePolicyOnly = false;
            return false;
        }
    }


    /**
    * CheckForBespokeOnly
    * Checks if order is just bespoke, and no prices
    */
    public CheckForBespokeOnly(products: Product[]): boolean {
        const firstCheck = this.CheckIfAllProductsInOrderAreBespoke(products);
        if (!firstCheck) {
            const bespoke = products.find(x => x.isBespokeProduct);
            const anyPrices = products.find(x => x.Price !== 0 || x.FromPrice !== 0);
            if (bespoke && !anyPrices) {
                this.BespokePolicyOnly = true;
                this.BespokePolicyOnly = true;
                return true;
            }
            this.BespokePolicyOnly = false;
            return false;
        } else {
            return firstCheck;
        }
    }

    UncheckBespokeAttributesForProduct(id: number): any {
        const product = this.getProductById(id);
        if (product !== undefined) {
            product.priceAttributes.forEach(attr => attr.bespokeAttribute = false);
        }
    }


    setProductToBespoke(productId: number): any {
        const product = this.getProductById(productId);

        if (product && productId !== 211) {

            product.FromPrice = 0;
            product.Price = 0;
            product.isBespokeProduct = true;
        }
    }

    CheckIfAttributesAreBespoke(attribute: PriceAttribute): boolean {
        const product = this.getProductById(attribute.productId);
        const any = product.priceAttributes.find(x => x.bespokeAttribute);

        if (any) {
            return true;
        }
        return false;
    }



    addBespokeAttributeOptionToProduct(attribute: PriceAttribute, product: Product) {
        if (product.bespokeAttributes === undefined) {
            product.bespokeAttributes = [];
        }

        const bespokeAttr = new BaseProductAttributes();
        bespokeAttr.id = attribute.id;
        bespokeAttr.name = attribute.name;
        bespokeAttr.formValue = attribute.formValue;
        bespokeAttr.productId = attribute.productId;
        bespokeAttr.type = 'text';
        bespokeAttr.required = true;
        bespokeAttr.description = attribute.description;
        bespokeAttr.sortorder = 998;
        bespokeAttr.bespokeAttribute = true;
        if (!product.bespokeAttributes.find(x => x.productId === bespokeAttr.productId && x.name === bespokeAttr.name)) {
            product.bespokeAttributes.push(bespokeAttr);
            this.bespokeAttributeAddSubject.next(bespokeAttr);
        }
    }


    removeBespokeAttributeOptionFromProduct(attribute: PriceAttribute, product: Product) {
        if (product.bespokeAttributes) {
            const attr = product.bespokeAttributes.find(x => x.productId === attribute.productId && x.name === attribute.name);
            const index = product.bespokeAttributes.indexOf(attr);
            product.bespokeAttributes.splice(index, 1);
            this.bespokeAttributeRemoveSubject.next(attr);
        }
    }


    removeBespokeFromProduct(productId: number): any {
        const product = this.getProductById(productId);
        if (product) {

            product.isBespokeProduct = false;
        }
    }


    getProductById(productId) {
        return this.Basket.find(x => x.productId === productId);
    }

    setPrice(price) {
        this.BasketPriceTotal = price;
    }



    ClearStates(): any {
        this.BasketPriceTotal = 0;
        this.CurrentTaxTotal = 0;
        this.Basket = new Array<Product>();
        this.PriceAttributes = new Array<PriceAttribute>();
        this.SofValid = false;
        this.applyVoucher = false;
        this.isBespoke = false;
        this.ProductList = new Array<Product>();
    }

    // For routing = madness
    sendNewInsurance(param: boolean) {
        this.isInsuaranceSubject.next(param);
    }

    /**
    * Update Individual product price on right hand panel
    * Changes individual product from price
    */
    updateProductPrice(id: number, newPrice: number): any {
        const foundProduct = this.Basket.find(x => x.productId === id);
        if (foundProduct) {
            foundProduct.FromPrice = newPrice;
            foundProduct.Price = newPrice;
        }
    }

    removeProductPriceFromTotal(id: number) {
        const foundProduct = this.Basket.find(x => x.productId === id);
        if (foundProduct) {
            if (this.BasketPriceTotal > 0) {
                // If this condition Matches then that means attributes have changed the price
                if (foundProduct.Price !== foundProduct.FromPrice) {
                    // Subtract price based of attr based price
                    this.BasketPriceTotal -= foundProduct.Price;
                } else {
                    this.BasketPriceTotal -= foundProduct.FromPrice;
                }
            }
        }
    }

    removeProductTaxFromTotal(productId: number): any {
        const foundProduct = this.Basket.find(x => x.productId === productId);
        if (foundProduct) {
            if (this.CurrentTaxTotal > 0) {
                this.CurrentTaxTotal -= Number(foundProduct.Ipt.toFixed(2));
            }
        }
    }


    addTaxToTaxTotal(id: number) {
        if (this.CurrentTaxTotal < 0) {
            this.CurrentTaxTotal = 0;
        }
        const foundProduct = this.Basket.find(x => x.productId === id);
        if (foundProduct) {
            this.CurrentTaxTotal += foundProduct.Ipt;
        }
    }


    addProductPriceToTotal(id: number) {
        if (this.BasketPriceTotal < 0) {
            this.BasketPriceTotal = 0;
        }

        const foundProduct = this.Basket.find(x => x.productId === id);
        if (foundProduct) {
            if (foundProduct.Price !== foundProduct.FromPrice) {
                // Subtract price based of attr based price
                this.BasketPriceTotal += foundProduct.Price;
            } else {
                this.BasketPriceTotal += foundProduct.FromPrice;
            }
        }
    }

    setValidation(isvalid: boolean) {
        this.SofValid = isvalid;
        this.isValidSubject.next(isvalid);
    }

    hasVoucher(applyvoucher: boolean) {
        this.applyVoucher = applyvoucher;
        this.applyVoucherSubject.next(applyvoucher);
    }

    setVoucherDiscount(_voucherDiscount: number) {
        this.voucherDiscount = _voucherDiscount;
        this.voucherDiscountSubject.next(_voucherDiscount);
    }

    removeDiscountPrice(productId: number) {
        this.discountPrices = this.discountPrices.filter((_item) => _item.id !== productId);
        this.discountPricesSubject.next(<DiscountPrices[]>this.discountPrices);
    }

    addDiscountPrice(discount: DiscountPrices) {
        this.discountPrices.push(discount);
        this.discountPricesSubject.next(<DiscountPrices[]>this.discountPrices);
    }

    addBespokeSOF(sofDisagree: SofDisagree) {
        if (this.bespokeSOF.findIndex(x => x.id === sofDisagree.id) === -1) {
            this.bespokeSOF.push(sofDisagree);
            this.bespokeSOFSubject.next(<SofDisagree[]>this.bespokeSOF);
        }
    }

    setSelectedAddress(_selectedAddress: Address) {
        this.selectedAddress = _selectedAddress;
        this.selectedAddressSubject.next(this.selectedAddress);
    }

    manualAddressEnteredValid() {
    }

    /**
    * CheckForBespokeProductsAndPriceProducts
    * Checks if product has price and if product selected is bespoke
    */
    public CheckForBespokeProductsAndPriceProducts(products: Product[]): boolean {
        // Find products that are bespoke
        const bespoke = products.find(x => x.isBespokeProduct);
        // Get products that arent bespoke and have price
        const anyPrices = products.find(x => (x.Price !== 0 || x.FromPrice !== 0) && x.isBespokeProduct === false);
        if (bespoke && anyPrices) {
            this.isBespoke = true;
            this.basketContainBespokeAndPrice.next(true);
            return true;
        }

        this.isBespoke = false;
        this.basketContainBespokeAndPrice.next(false);
        return false;
    }

    ClearProductPriceAndBracket(res: Product): any {

        res.isBespokeProduct = false;
        res.Price = 0;
        res.FromPrice = 0;
        res.loiBracketValue = 0;
        res.loiMinValue = 0;
        res.checked = false;
        this.BespokePolicyOnly = false;
    }


    /**
    * productListContainBespoke
    * Checks if order is only bespoke
    */
    productListContainBespoke(isBespoke: boolean) {
        this.BespokePolicyOnly = isBespoke;
    }

    validatePlaceOrderFromAttributes(valid: boolean, caseAddress) {
        if (!caseAddress) {
            valid = false;
        }
        this.policyFormValid = valid;
    }

    validatePlaceOrderBtnFromAttributes(valid: boolean) {
        this.productAttributesFormValid = valid;
    }

    validateDraftBtnFromAttributes(isValid: boolean) {
        this.draftAttributesFormValid = isValid;
    }
    /**
 * GetPriceFromAttribute
 * Sets new total price from attribute change
 * Also gets called when adding product with attribute
 */
    public GetPriceFromAttribute(attribute: PriceAttribute, value, passedInP: Product) {
        const product = this.GetProductFromAttribute(attribute);
        this.AddingDraftAttribute(passedInP, attribute, product);
        const loiBracket = product.loiBracketValue;

        if (attribute.isCommonAttribute) {
            // Common attribute
            this.WorkoutProductPricesFromCommonAttribute(product, loiBracket, value, attribute);
        } else {
            return this.WorkoutPriceFromAttribute(product, loiBracket, value, attribute, passedInP);
        }
    }


    public AddingDraftAttribute(passedInP: Product, attribute: PriceAttribute, productFromAttribute: Product) {
        // This is normally when resuming draft
        if (productFromAttribute === undefined && passedInP !== undefined) {
            // Adding price attribute for draft
            const checkForDuplicate = this.PriceAttributes.find(x => x.name === attribute.name);
            if (!checkForDuplicate) {
                this.PriceAttributes.push(attribute);
            }
            productFromAttribute = passedInP;
        }
    }

    public WorkoutProductPricesFromCommonAttribute(product: Product, loiBracket, value, attribute: PriceAttribute): number {
        this.Basket.forEach(prod => {
            loiBracket = prod.loiBracketValue;
            if (prod.commonPriceAttributes) {
                const commonPriceAttributes = prod.commonPriceAttributes.find(x => x.isCommonAttribute && x.name === attribute.name);
                if (commonPriceAttributes) {
                    commonPriceAttributes.formValue = value;
                    commonPriceAttributes.value = value;
                    const priceObj = this.FindPriceFromAttributes(prod, loiBracket, commonPriceAttributes, value, true);
                    prod.Price = priceObj.totalPrice;
                    prod.Ipt = priceObj.totalIpt;
                }
            }
        });
        this.BasketPriceTotal = this.TotalProductPricesAndAttributePrice();
        this.CurrentTaxTotal = this.TotalProductTax();

        return this.BasketPriceTotal;
    }

    // This is getting called in multiple places - gets price of one product
    public WorkoutPriceFromAttribute(product: Product, loiBracket, value, attribute: PriceAttribute, passedInP) {
        // Gets price per one product
        const priceObj = this.FindPriceFromAttributes(product, loiBracket, attribute, value, false);
        product.Price = priceObj.totalPrice;
        product.Ipt = priceObj.totalIpt;
        if (priceObj.totalPrice === 0) {
            // new product has got attributes which determine price - hence price 0
            if (!product.FromPrice) {
                product.FromPrice = 0;
            }

            this.BasketPriceTotal -= product.FromPrice;
            // Subtract existing ipt
            product.Price = 0;
            product.FromPrice = 0;
            this.BasketPriceTotal = this.TotalProductPricesAndAttributePrice();
            this.CurrentTaxTotal = this.TotalProductTax();
            this.productIsZero.next(true);
        } else {
            this.productIsZero.next(false);
            product.Price = priceObj.totalPrice;

            if (this.Basket.length === 1) {
                this.CurrentTaxTotal = 0;
            }

            // Adding new found price from attributes with minimum product prices
            this.BasketPriceTotal = this.TotalProductPricesAndAttributePrice();
            this.CurrentTaxTotal = this.TotalProductTax();

            // Sanity Check - tax and price
            if (this.BasketPriceTotal < 0) {
                this.BasketPriceTotal = 0;
            }
            if (this.BasketPriceTotal < 0) {
                this.BasketPriceTotal = 0;
            }
            if (this.CurrentTaxTotal < 0) {
                this.CurrentTaxTotal = 0;
            }

        }


        return priceObj;
    }

    public GetProductFromAttribute(attr: PriceAttribute): Product {
        return this.Basket.find(x => x.productId === attr.productId);
    }

    public ProductIsBespoke(_product: Product, attributeChanged: PriceAttribute, value) {
    }

    private BuildAttributesForPriceCheck(loiBracket: number, product: Product, commonAttribute: boolean,
        value: string, attribute: PriceAttribute): PriceAttribute[] {
        let loi = '';
        if (loiBracket) {
            loi = loiBracket.toString();
        }
        let productPriceAttributes;
        if (commonAttribute) {
            productPriceAttributes = product.commonPriceAttributes.filter(x => x.productId === product.productId);
        } else {
            productPriceAttributes = product.priceAttributes.filter(x => x.productId === product.productId);
        }

        // Setting the value on the price attribute
        const formAttributesPerProduct = new Array<PriceAttribute>();
        productPriceAttributes.forEach(function (formAttr: PriceAttribute) {
            if (formAttr.name === attribute.name) {
                formAttr.value = value;
                formAttr.formValue = value;
            } else if (formAttr.value === undefined) {
                formAttr.value = formAttr.options[0].value;
            }
            if (!formAttributesPerProduct.find(x => x.name === formAttr.name)) {
                formAttributesPerProduct.push(formAttr);
            }
        });

        // Need to add common attribute for pricing
        if (product.commonPriceAttributes && product.commonPriceAttributes.length > 0) {
            product.commonPriceAttributes.forEach(function (formAttr: PriceAttribute) {
                const attributesForPricing = formAttributesPerProduct.find(x => x.name === formAttr.name);
                if (attributesForPricing === undefined) {
                    formAttr.value = formAttr.formValue;

                    if (!formAttributesPerProduct.find(x => x.name === formAttr.name)) {
                        formAttributesPerProduct.push(formAttr);
                    }
                }
            });

            // Need to add specific attributes too to get a price if there is any
            product.priceAttributes.forEach(attr => {
                if (attr.formValue === undefined || attr.formValue === '' && attr.options) {
                    const defaulOption = attr.options.find(x => x.default);
                    if (defaulOption) {
                        attr.formValue = defaulOption.value;
                    }
                }

                if (!formAttributesPerProduct.find(x => x.name === attr.name)) {
                    formAttributesPerProduct.push(attr);
                }
            });
        }

        // If common attribute need to set the attr value on the other attribute on the other products
        if (commonAttribute) {
            if (product.commonPriceAttributes) {
                const common = product.commonPriceAttributes.find(x => x.name === attribute.name);
                if (common) {
                    common.value = value;
                }
            }
        }

        //explicit cheack for TP356: Breach of Section 106 or 52 Agreement
        if(product.productId == 356 && attribute.name == "SOFCONTINUEDUSE"){
            //If so, add the "SOFCONTINUEDUSE" attribute
            attribute.bespokeAttribute = true;
            attribute.isHidden = false;
            attribute.value = value;
        }
        // If no LOI attr add one in
        if (formAttributesPerProduct.find(x => x.name !== 'LIMITOFINDEMNITY') || formAttributesPerProduct.length === 0) {
            const loiAttr = new PriceAttribute();
            loiAttr.name = 'LIMITOFINDEMNITY';
            loiAttr.value = loi;
            loiAttr.productId = product.productId;
            formAttributesPerProduct.push(loiAttr);
        }
        
        return formAttributesPerProduct;
    }

    public PerformPriceCalculation(enteredLoiValue) {
        this.loi = enteredLoiValue.replace(/[^\d.]/g, '');
        this.Basket.forEach(product => {
            // If product price attributes is empty - its because the products price is only dictated by the LOI
            if (product.priceAttributes.length !== 0) {
                this.GetPriceFromAttribute(product.priceAttributes[0], product.priceAttributes[0].formValue, product);
            } else {
                // Price determined by only LOI - but the invoking method will double check anyway
                const priceLoiAttribute: PriceAttribute = new PriceAttribute();
                priceLoiAttribute.name = 'LIMITOFINDEMNITY';
                if (product.loiBracketValue) {
                    priceLoiAttribute.value = product.loiBracketValue.toString();
                } else {
                    priceLoiAttribute.value = undefined;
                }
                if (product.loiBracketValue) {
                    priceLoiAttribute.formValue = product.loiBracketValue.toString();
                } else {
                    priceLoiAttribute.formValue = undefined;
                }
                priceLoiAttribute.productId = product.productId;
                this.GetPriceFromAttribute(priceLoiAttribute, priceLoiAttribute.formValue, product);
            }
        });

    }



    // tslint:disable-next-line: max-line-length
    // Finding price from attribute combination
    // All logic for attribute changing works out priceing
    public FindPriceFromAttributes(product: Product, loiBracket: number, attribute: PriceAttribute, value: any, commonAttribute: boolean) {
        const formAttributesPerProduct = this.BuildAttributesForPriceCheck(loiBracket, product, commonAttribute, value, attribute);
        // if total price is 0 then double check for different working product - price made up of attribute to find loi
        // Looking for price for normal products like absent landlord
        let totalPriceObj = this.FindPriceFromAttributeChange(product, formAttributesPerProduct);

        if (totalPriceObj === undefined) {
            totalPriceObj = {
                totalPrice: 0,
                totalIpt: 0
            };
        }

        // If total price is 0, do a check for attributes that make loi, ignores loi bracket - for products like Contaminated Land
        // Attribute combination decides loi as all the attribute combinations are "unique"
        // Double check - works out pricing for contaminated land for example
        // #RULES AT TOP OF CLASS
        if (totalPriceObj.totalPrice === 0) {
            totalPriceObj = this.CheckPriceAttributesDetermineLOI(product, formAttributesPerProduct);
        } else {
            // Price found from first check - so not bespoke
            product.isBespokeProduct = false;
            this.setProductIsBespoke(false, product.productId);
        }

        if (!product.stateFact && product.statementOfFactAcknowledged) {
            // The product must remain bespoke.
            product.isBespokeProduct = true;
        }

        // if price is 0 and not bespoke
        if (totalPriceObj.totalPrice === 0 && !product.isBespokeProduct) {
            // Invalid combination
            product.isBespokeProduct = false;
            this.setProductIsBespoke(false, product.productId);
            product.invalidProductAttributes = true;
        } else if (product.isBespokeProduct && !product.invalidProductAttributes) {
            product.isBespokeProduct = true;
            this.setProductIsBespoke(true, product.productId);
            product.invalidProductAttributes = false;
        } else if (product.isBespokeProduct && product.invalidProductAttributes) {
            // if bespoke and invalid - set bespoke false
            product.isBespokeProduct = false;
        }

        return totalPriceObj;
    }

    public FindPriceFromAttributeChange(product: Product, formAttributesPerProduct: PriceAttribute[]) {
        product.isBespokeProduct = false;
        product.invalidProductAttributes = false;
        // Find price
        let totalPrice = 0;
        let totalIpt = 0;
        // If formvalue not set - then take value as that probably is.
        formAttributesPerProduct.forEach(fAttr => {
            if (!fAttr.formValue) {
                fAttr.formValue = fAttr.value;
            }
        });

        // Where attributes can't match and find a price
        const notMatching = [];

        if (!product?.prices) {
            product.prices = [];
        }

        product?.prices?.forEach(function (p, index) {
            const i = index;
            const price = p.price;
            const ipt = p.ipt;
            const matchingAttributes = [];
            let foundPrice = false;
            const attributecount = p.attributes.length;
            p.attributes.forEach(function (attr) {
                if (!foundPrice) {
                    formAttributesPerProduct.forEach(function (formAttr) {
                        if (attr.name === formAttr.name && attr.value === formAttr.formValue) {
                            matchingAttributes.push(formAttr);
                            if (matchingAttributes.length === attributecount) {
                                foundPrice = true;
                                totalPrice = price;
                                totalIpt = ipt;
                            } else {
                                // Check it doesnt contain
                                if (notMatching && notMatching.length > 0) {
                                    const noMatchIndex = notMatching.find(x => x.name === attr.name && x.formValue === attr.value);
                                    if (!noMatchIndex) {
                                        notMatching.push(attr);
                                    }
                                } else {
                                    notMatching.push(attr);
                                }
                            }
                        }
                    });
                }
            });
        });

        // IMPORTANT CHECK HERE
        // If the policy has 1 default LOI that isnt price related it will return that value.
        // Which will be the MAX loi value that can be covered for this policy - regardless of the price attributes
        const defaultLoiValue = this.GetProductDefaultLoiValue(product.productId);

        // If its set, then that means a default LOI with 1 attribute value has been returned.
        if (defaultLoiValue !== 0) {
            // SO we need to make sure that the entered LOI is less than the default LOI - otherwise its bespoke.
            if (parseFloat(this.loi) > defaultLoiValue) {
                // If the entered LOI is greater than the default, then its bespoke regardless.
                product.isBespokeProduct = true;
            } else {
                // if entered loi is lower than default LOI then its not bespoke
                product.isBespokeProduct = false;
            }
        }

        // Check for invalid
        const isLoiGreaterThanBracket = this.EnteredLoiIsGreatedThanHighestBracket(this.loi, product.prices);
        if (totalPrice === 0) {
            if (product.productId === 221) {
                if (isLoiGreaterThanBracket) {
                    product.invalidProductAttributes = false;
                    product.isBespokeProduct = true;
                }
            } else if (isLoiGreaterThanBracket) {
                product.isBespokeProduct = true;
            }
        }

        // Handle any unique products hee. Above is the general business knowledge for majority products.
        // Japanese Knotweed - if the LOI entered is greater than the highest bracket - we dont follow bespoke funcitonality
        // And have to show the one price
        const priceObject = this.UniqueScenariosPerProduct(product, isLoiGreaterThanBracket);
        // If price has been manually set based of unique implementation
        if (priceObject) {
            return priceObject;
        }


        if (product.isBespokeProduct || (!product.stateFact && product.statementOfFactAcknowledged) || product.invalidProductAttributes) {
            return {
                totalPrice: 0,
                totalIpt: 0
            };
        }

        if (totalPrice === undefined) {
            totalPrice = 0;
        }

        return {
            totalPrice: totalPrice,
            totalIpt: totalIpt
        };
    }

    public UniqueScenariosPerProduct(product: Product, loiGreaterThanBracket) {
        let totalPrice = 0;
        let totalIpt = 0;
        switch (product.productId) {
            case 250: {
                // Personal Search Insurance - Has no price related Attributes...
                if (product.priceAttributes.length === 0 && product.prices.length === 1) {
                    return {
                        totalPrice: product.prices[0].price,
                        totalIpt: product.prices[0].ipt
                    };
                }
                break;
            }
            default: {
                return undefined;
            }
        }
    }

    public EnteredLoiIsGreatedThanHighestBracket(enteredLoi: string, prices: Prices[]): boolean {
        let foundLoiValues = [];
        for (let index = 0; index < prices.length; index++) {
            const p = prices[index];
            const loiAttributes = p.attributes.filter(
                x => x.name === 'LIMITOFINDEMNITY'
            );

            if (loiAttributes) {
                loiAttributes.forEach(attr => {
                    foundLoiValues.push(Number(attr.value));
                });
            }
        }

        if (foundLoiValues) {
            const largestValue = Math.max(...foundLoiValues);
            const loi = parseFloat(enteredLoi);
            if (loi > largestValue) {
                return true;
            }
        }

        return false;
    }


    public FindPriceFromAttributeChangeGetObj(product: Product, formAttributesPerProduct: PriceAttribute[]): PriceAttribute[] {
        // Find price
        let totalPrice = 0;
        let foundAttributes = new Array<PriceAttribute>();
        // Where attributes cant match and find a price
        const notMatching = [];
        product.prices.forEach(function (p, index) {
            const i = index;
            const price = p.price;
            const priceAttributes = p.attributes;
            const matchingAttributes = [];
            let foundPrice = false;
            const attributecount = p.attributes.length;
            p.attributes.forEach(function (attr) {
                if (!foundPrice) {
                    formAttributesPerProduct.forEach(function (formAttr) {
                        if (attr.name === formAttr.name && attr.value === formAttr.value) {
                            matchingAttributes.push(formAttr);
                            if (matchingAttributes.length === attributecount) {
                                foundPrice = true;
                                totalPrice = price;
                                foundAttributes = priceAttributes;
                            } else {
                                // Check it doesnt contain
                                if (notMatching && notMatching.length > 0) {
                                    const noMatchIndex = notMatching.find(x => x.name === attr.name && x.value === attr.value);
                                    if (!noMatchIndex) {
                                        notMatching.push(attr);
                                    }
                                } else {
                                    notMatching.push(attr);
                                }
                            }
                        }
                    });
                }
            });
        });

        return foundAttributes;
    }

    // Works out logic for contaminated land
    public CheckPriceAttributesDetermineLOI(product: Product, chosenAttributes: PriceAttribute[]) {
        let foundNewPrice = 0;
        let foundPriceObj;
        let foundCombination = false;
        let passedInLoi = '';

        // Other products are also using this method,
        // so ensure that adding this won't break any other unique scenarios for other products, restricting this to 221.
        if (product.productId === 221) {
            const loiAttributeExist = product.attributes.find((attr) => attr.name === "LIMITOFINDEMNITY");

            if (!loiAttributeExist) {
                const loiAttrExistInNonPricingAttrs =
                    product?.nonPriceAttributes?.find((attr) => attr.name === "LIMITOFINDEMNITY");

                if (loiAttrExistInNonPricingAttrs) {
                    product.attributes.push({
                        name: "LIMITOFINDEMNITY",
                        value: loiAttrExistInNonPricingAttrs.formValue,
                        values: loiAttrExistInNonPricingAttrs.values,
                        productId: loiAttrExistInNonPricingAttrs.productId,
                        id: loiAttrExistInNonPricingAttrs.id,
                    });
                }
            }
        }

        // Total price is 0 at this point
        // Lets find out other loi values
        if (product.isBespokeProduct || (!product.stateFact && product.statementOfFactAcknowledged) || product.invalidProductAttributes) {
            return {
                totalPrice: 0,
                totalIpt: 0
            }
        }

        const loiAttribute = product.attributes.find(x => x.name === 'LIMITOFINDEMNITY');

        if (loiAttribute && loiAttribute.values) {
            // got loi attribute
            // get other loi values and see if we can find a price
            let chosenAttibuteLoi = chosenAttributes.find(x => x.name === 'LIMITOFINDEMNITY');
            if (chosenAttibuteLoi) {
                passedInLoi = chosenAttibuteLoi.value;
                for (let index = 0; index < loiAttribute.values.length; index++) {
                    let loiValue = loiAttribute.values[index];
                    if (loiValue) {
                        // Need to set chosen loi to iterated loi for products like CL where combination could be for that loi
                        chosenAttibuteLoi.value = loiValue.value;
                        chosenAttibuteLoi.formValue = loiValue.value;
                        const foundAttr = this.FindPriceFromAttributeChangeGetObj(product, chosenAttributes);
                        const newPrice = this.FindPriceFromAttributeChange(product, chosenAttributes);
                        // new price found
                        if (newPrice.totalPrice !== 0) {
                            // check for bespoke
                            const foundCombinationLoi = foundAttr.find(x => x.name === 'LIMITOFINDEMNITY');
                            if (foundCombinationLoi && parseFloat(foundCombinationLoi.value) < parseFloat(this.loi)) {
                                // bespoke
                                foundCombination = true;
                                foundNewPrice = 0;
                                product.isBespokeProduct = true;
                                break;
                            } else {

                                foundCombination = true;
                                foundNewPrice = newPrice.totalPrice;
                                foundPriceObj = newPrice;
                                product.isBespokeProduct = false;
                                product.invalidProductAttributes = false;
                                product.loiBracketValue = parseFloat(chosenAttibuteLoi.value);
                                break;
                            }
                        }
                    }
                }
            }
        }
        // no combination found, so invalid attribute
        if (!foundCombination) {
            product.isBespokeProduct = false;
            this.setProductIsBespoke(false, product.productId);
            product.invalidProductAttributes = true;
            return {
                totalPrice: 0,
                totalIpt: 0
            }
        }

        if (foundPriceObj !== undefined) {
            return foundPriceObj;
        }

        return {
            totalPrice: 0,
            totalIpt: 0
        };
    }

    public FindIptFromAttrPrice(price: number, prices: Prices[], id: number): number {
        let newIpt = 0;
        const foundPrice = prices.find(x => x.price === price);
        if (foundPrice !== undefined) {
            newIpt = foundPrice.ipt;
            return newIpt;
        }
        return newIpt;
    }

    /**
    * Resets total price then totals individual product prices
    * Sets new total price from attribute change
    * Also gets called when adding product with attribute
    */
    public TotalProductPricesAndAttributePrice(): number {
        let newTotal = 0;
        this.Basket.forEach(p => {
            // If product bespoke
            if ((!p.stateFact && p.statementOfFactAcknowledged) || p.isBespokeProduct || (p.isBespokeProduct && p.Price > 0)) {
                return;
            }
            if (p.Price !== p.FromPrice) {
                newTotal += p.Price;
            } else {
                newTotal += p.FromPrice;
            }
        });

        return newTotal;
    }

    public TotalProductTax() {
        let newTotal = 0;
        this.Basket.forEach(p => {
            // If product bespoke
            if ((!p.stateFact && p.statementOfFactAcknowledged) || p.isBespokeProduct || (p.isBespokeProduct && p.Price > 0)) {
                return;
            }
            newTotal += p.Ipt;
        });

        return newTotal;
    }

    /// Reshuffling non price attributes
    public ReShuffleNonPriceCommonAttributes(productRemoved: Product) {
        // This method needs to work out which common attributes to remove from the policy detail component
        const commonAttributes = productRemoved.commonNonPriceAttributs;
        // productRemoved.Price = productRemoved.FromPrice;

        if (commonAttributes) {
            const allCommonAttributes = new Array<BaseProductAttributes>();
            // Product has common attributes, find which products are common
            this.Basket.filter(x => x.productId !== productRemoved.productId).forEach(prod => {
                // If product has common common attributes
                if (prod.commonNonPriceAttributs) {
                    // Find matching common attribute, and move it into price attributes
                    commonAttributes.forEach(commonAttr => {
                        const foundMathing = prod.commonNonPriceAttributs.find(x => x.isCommonAttribute && x.name === commonAttr.name);
                        allCommonAttributes.push(foundMathing);
                    });
                }
            });
            // Get count of products left based off all common attributes based of product removed
            const distinctedProductIds = allCommonAttributes.filter(x => x !== undefined).map(p => p.productId)
                .filter((productid, index, arr) => arr.indexOf(productid) === index);

            // If only 1 product id left, then need to push the common attributes into that products price attributes
            if (distinctedProductIds && distinctedProductIds.length === 1) {
                const getproduct = this.Basket.find(x => x.productId === distinctedProductIds[0]);
                getproduct.commonNonPriceAttributs.forEach(commonAttr => {
                    commonAttr.isCommonAttribute = false;
                    getproduct.nonPriceAttributes.push(commonAttr);

                });
                getproduct.commonNonPriceAttributs = new Array<BaseProductAttributes>();
                // Get count of non hidden non price attributes
                const hiddenCount = getproduct.nonPriceAttributes.filter(x => x.hidden === true)
                    .length;
                if (getproduct.nonPriceAttributes.length === hiddenCount) {
                    getproduct.allAttrHidden = true;
                } else {
                    getproduct.allAttrHidden = false;
                }
            }
            productRemoved.commonNonPriceAttributs = new Array<BaseProductAttributes>();
            productRemoved.nonPriceAttributes = new Array<BaseProductAttributes>();

        }

        // Go back to policy detial form and rebuild form controls based of the now updated basket.
        this.rebuildPolicyDetailForm.next(this);
    }


    public ReShuffleCommonAttributes(productRemoved: Product) {
        // Check if this product has common attributes
        const commonAttributes = productRemoved.commonPriceAttributes;
        // productRemoved.Price = productRemoved.FromPrice;
        commonAttributes.forEach(attr => {
            if (this.CommonPriceAttributes) {
                this.CommonPriceAttributes = this.CommonPriceAttributes.filter(x => x.name !== attr.name && x.productId !== attr.productId);
            }
        });

        if (commonAttributes) {
            const allCommonAttributes = new Array<PriceAttribute>();
            // Product has common attributes, find which products are common
            this.Basket.filter(x => x.productId !== productRemoved.productId).forEach(prod => {
                // If product has common common attributes
                if (prod.commonPriceAttributes) {
                    // Find matching common attribute, and move it into price attributes
                    commonAttributes.forEach(commonAttr => {
                        const foundMathing = prod.commonPriceAttributes.find(x => x.isCommonAttribute && x.name === commonAttr.name);
                        allCommonAttributes.push(foundMathing);
                    });
                }
            });
            // Get count of products left based off all common attributes based of product removed
            const distinctedProductIds = allCommonAttributes.map(p => p.productId)
                .filter((productid, index, arr) => arr.indexOf(productid) === index);
            // If only 1 product id left, then need to push the common attributes into that products price attributes
            if (distinctedProductIds && distinctedProductIds.length === 1) {
                const getproduct = this.Basket.find(x => x.productId === distinctedProductIds[0]);
                getproduct.commonPriceAttributes.forEach(commonAttr => {
                    commonAttr.isCommonAttribute = false;
                    getproduct.priceAttributes.push(commonAttr);
                });
                getproduct.commonPriceAttributes = new Array<PriceAttribute>();
                this.commonPriceAttributeSubject.next(new Array<PriceAttribute>());
            }
            productRemoved.commonPriceAttributes = new Array<PriceAttribute>();
            productRemoved.priceAttributes = new Array<PriceAttribute>();
        }
    }


    public DisposeServiceData() {

        this.postOrder = false;
        this.selectedAddress = null;
        this.voucherDiscount = 0;
        this.CommonPriceAttributes = [];
        this.CurrentDrafts = [];
        this.invalidAttrCombinationOnly = false;
        this.BasketPriceTotal = 0;
        this.CurrentTaxTotal = 0;
        this.IsInsuranceComponent = false;
        this.PriceAttributes = [];
        this.productAddedFromList = false;
        if (this.ProductList) {
            this.ProductList.forEach(prod => {
                prod.checked = false;
                prod.isBespokeProduct = false;
                if (prod.attributes) {
                    prod.attributes.forEach(attr => {
                        attr.formValue = '';
                    });
                }

                prod.bespokeAttributes = [];
                prod.nonPriceAttributes = [];
                prod.commonPriceAttributes = [];
                prod.priceAttributes = [];
                prod.commonNonPriceAttributs = [];
            });
        }
        this.Basket = [];
        this.addToExistingCase = false;
        this.applyVoucher = false;
        this.bespokeSOF = [];
        // this.customLoi = '';
        this.discountPrices = [];
        this.draftCaseId = '';
        this.isBespoke = false;
        this.SofValid = false;
        this.lineGuid = '';
        this.loi = '';
        this.draftCaseId = '';
        this.existingCaseId = undefined;
        this.existingOrderId = undefined;
        this.productDefaultLois = [];
    }

    public HasProductGotLOIPriceAttr(product: Product): boolean {
        const noLoiPriceAttribute = product.prices[0];
        const pricesGotLoiAttr = noLoiPriceAttribute.attributes.find(x => x.name === 'LIMITOFINDEMNITY');
        if (pricesGotLoiAttr) {
            return true;
        }
        return false;
    }

    public IsBespokePopupAttributeSelected() {
        this.popupProducts = [];
        this.bespokeAttProducts = [];
        for (let h = 0; h < this.Basket.length; h++) {
            const product = this.Basket[h];
            const allPriceDropDowns = this.getAllPriceAtrributes(product.commonPriceAttributes, product.priceAttributes)
            if (allPriceDropDowns.length > 0) {
                this.checkAllDropDowns(allPriceDropDowns, product)
            } else {
                continue;
            }
        }
        if (this.popupProducts.length > 0) {
            this.ShowAttributesPopUp(this.popupProducts);
        } else if (this.bespokeAttProducts.length > 0) {
            this.SetProductToBespokeAfterPrice(this.bespokeAttProducts)
        }
        this.CheckProductsAreBespokeAfterPrice(this.Basket);
    }

    checkAllDropDowns(allPriceDropDowns:PriceAttribute[], product:Product) {
        for (let i = 0; i < allPriceDropDowns.length; i++) {
            const priceDropdown = allPriceDropDowns[i];
            for (let j = 0; j < priceDropdown.options.length; j++) {
                // Current attribute value for iteration,
                const attrValue = priceDropdown.options[j].value;
                // Check the is isbespokeprice flag
                const isBespoke: boolean = priceDropdown.options[j].isBespokePrice;
                // Popup value
                const popUpValue = 'N/A';
                // Get selected value
                const selectedValue = allPriceDropDowns[i].formValue;

                const dropDownName = allPriceDropDowns[i].name
                // Check for bespoke
                if (attrValue === selectedValue && isBespoke) {
                    this.bespokeAttProducts.push(product);
                }
                // Check for popup.
                else if (attrValue === selectedValue && selectedValue === popUpValue) {
                    const popupObj = { productname: product.name, dropdown: dropDownName, isresidential: product.residential, productId: product.productId, attrValue:selectedValue };
                    const duplicate = this.popupProducts.find(prod => prod.productname === popupObj.productname && prod.isresidential === popupObj.isresidential);
                    if (popupObj.productname && !duplicate) {
                        this.popupProducts.push(popupObj);
                    }
                }
            }
        }

    }

    SetProductToBespokeAfterPrice(products: Product[]) {
        for (let i = 0; i < products.length; i++) {
            const product = products[i];
            this.removeProductPriceFromTotal(product.productId);
            this.removeProductTaxFromTotal(product.productId);
            this.setProductToBespoke(product.productId);
            this.SettingProductToValid(product);
        }
    }

    SettingProductToValid(product) {

        product.invalidProductAttributes = false;
        this.invalidAttrCombinationOnly = false;
        product.priceAttributes.forEach(function (attr) {
            attr.invalidAttribute = false;
        });
    }

    ShowAttributesPopUp(popupProducts) {

        const productsLen = popupProducts.length;
        let isMultipleProducts = productsLen > 1 ? 'policies' : 'policy';
        let isResidential = popupProducts[0].isresidential ? 'residential' : 'commercial';
        this.productNamePopup = '';
        this.showAttributePopup = true;
        this.productNamePopup = this.getPopupNameText(productsLen, popupProducts);
        //Will need this for the other popups
        const commencialResidentialText = 'Please refer to the ' + isResidential + ' equivalent insurance ' + isMultipleProducts + '.';
        const searchInsuranceText = 'The option you have selected would indicate that the property is a new build, please refer to Search Insurance (New Build/Works Carried out in Last 12 Months) which may be the correct option for you.';
        
        //This product has two popups i dont know if this wil work on uat live
        if (popupProducts[0].dropdown === 'SOFPROPERTYUSE') {
            this.popupText = searchInsuranceText;
        } else {
            this.popupText = commencialResidentialText;
        }
        const planningPermissionProducts = [349, 350];
        const conservationAreaProducts = [354, 355];

        if(popupProducts.find(p => planningPermissionProducts.find(pp => pp === p.productId && p.dropdown === "SOFLBCORCAC" && p.attrValue === "N/A"))){
            this.popupText = 'Please refer to our standard online Lack of Listed Building Consent and/or Lack of Conservation Area Consent policy which offers extensive cover for this risk.';
            
        } else if (popupProducts.find(p => p.dropdown==="SOFCONTINUEDUSE" && p.attrValue === "N/A" && p.productId === 349)){
            this.popupText = "Please refer to the commercial equivalent insurance policy.";
        }
        else if (popupProducts.find(p => conservationAreaProducts.find(pp => pp === p.productId && p.attrValue === "N/A" && p.dropdown === "SOFLB"))){
            this.popupText = 'Please refer to our standard online Lack of Listed Building Consent which offers extensive cover for this risk.';
            
        } else if(popupProducts.find(p => p.dropdown==="SOFCONTINUEDUSE" && p.attrValue === "N/A" && p.productId === 354)){
            this.popupText = "Please refer to the commercial equivalent insurance policy.";
        }
        window.scroll(0, 0);
    }

    getPopupNameText(productslength, products) {

        let popupStringName = '';
        if (productslength > 1) {
            for (let a = 0; a < productslength; a++) {
                popupStringName += products[a].productname + ','; 
            }
            popupStringName = popupStringName.replace(/.$/, '.');
        } else {
            popupStringName = products[0].productname + '.';
        };
        return popupStringName;
    }

    getAllPriceAtrributes(commonPriceAttributes:PriceAttribute[], priceAttributes:PriceAttribute[]) {

        let prices:PriceAttribute[] = [];
        if (priceAttributes) {
            prices = prices.concat(priceAttributes);
            if (commonPriceAttributes) {
                prices = prices.concat(commonPriceAttributes);
            }
        }
        return prices;
    }

    CheckProductsAreBespokeAfterPrice(products: Product[]): any {

        const invalidAttribute = products.filter(x => x.invalidProductAttributes).length;
        const bespokeCount = products.filter(x => x.isBespokeProduct || (x.stateFact === false && x.statementOfFactAcknowledged)).length;
        this.CheckIfValid(invalidAttribute, bespokeCount, products);
    }

    private CheckIfValid(productInvalidAttributeLength: number, bespokeProductLength: number, basket: Product[]) {

        // if there is a invalid just return
        if (productInvalidAttributeLength) {
            this.setValidation(false);
            return;
        }

        if (bespokeProductLength >= 1) {
            this.BespokeChecks(bespokeProductLength, basket);
        } else {
            this.BespokePolicyOnly = false;
            this.basketContainBespokeAndPrice.next(false);
            if (basket.every(x => x.statementOfFactAcknowledged)) {
                this.setValidation(true);
            }
        }

        // Always check if any invalids last
        if (productInvalidAttributeLength) {
            this.setValidation(false);
        }
    }


    private BespokeChecks(bespokeProductLength: number, basket: Product[]) {

        // If bespokes are same as basket length - then everything is bespoke
        if (bespokeProductLength === basket.length) {
            this.BespokePolicyOnly = true;
            this.basketContainBespokeAndPrice.next(false);
        } else {
            // Bespoke and normal order
            this.BespokePolicyOnly = false;
            this.basketContainBespokeAndPrice.next(true);
            const getDisAgreedSof = this.bespokeSOF.filter(disAgreed => disAgreed.isDisAgreed);

            if (getDisAgreedSof.length > 0) {

                const allFalseHaveReason = getDisAgreedSof.every(getReason => getReason.DisagreeReason !== "" && getReason.DisagreeReason !== null)

                if (basket.every(x => x.statementOfFactAcknowledged) && allFalseHaveReason) {
                    this.setValidation(true);
                }

            }
        }
        this.CheckBespokeProductSOFValid(basket);
    }

    private CheckBespokeProductSOFValid(basket: Product[]) {
        let reasonGiven: boolean;

        // Products are bespoke so check if reasons given
        for (let i = 0; i < basket.length; i++) {
            const bespokeProduct = basket[i];

            const getDisAgreedSof = this.bespokeSOF?.filter(disAgreed => disAgreed?.isDisAgreed);

            if (
                bespokeProduct.stateFact === false &&
                bespokeProduct.statementOfFactAcknowledged
            ) {
                if (this.bespokeSOF.length === 0) {
                    reasonGiven = false;
                    this.setValidation(false);
                }

                if (getDisAgreedSof.length > 0) {
                    const allFalseHaveReason = getDisAgreedSof.every(
                        (getReason) =>
                            getReason.DisagreeReason !== "" &&
                            getReason.DisagreeReason !== null
                    );
                    const everySofAcknowledged = basket.every(
                        (product) => product.statementOfFactAcknowledged
                    );
                    reasonGiven = allFalseHaveReason;
                    this.setValidation(allFalseHaveReason && everySofAcknowledged);
                }
            } else if (getDisAgreedSof.length === 0) {
                const everySofAcknowledged = basket.every(
                    (product) => product.statementOfFactAcknowledged
                );
                this.setValidation(everySofAcknowledged);
            }
        }
    }


    /**
     * isAttributeShared
     * Performs all checks as to if the attribute is shared or not
     * Returns shared attribute
     */
    public isAttributeShared(currentAttribute: PriceAttribute, compareArray: PriceAttribute[]): PriceAttribute {

        if (!currentAttribute || !compareArray) {
            return undefined;
        }

        if (!currentAttribute.productId) {
            return undefined;
        }

        return compareArray.find(
            x => x.name === currentAttribute.name &&
                x.productId !== currentAttribute.productId && this.IsAttributeValuesTheSame(currentAttribute.options, x.options)
        );
    }

    public IsAttributeValuesTheSame(currentAttributeValues: BaseProductAttributesValues[],
        compareArrayValues: BaseProductAttributesValues[]): boolean {

        if (!currentAttributeValues || currentAttributeValues.length === 0
            || !compareArrayValues || compareArrayValues.length === 0) {
            return false;
        }
        if (currentAttributeValues.length !== compareArrayValues.length) {
            return false;
        }

        // Compare array attribute values - value.
        // Get current attribute value values and compare.
        const currentAttributeValuesValues = currentAttributeValues.map(x => x.value);
        const compareArrayValuesValues = compareArrayValues.map(x => x.value);

        if (currentAttributeValuesValues.includes(undefined) || compareArrayValuesValues.includes(undefined)) {
            return false;
        }

        // Extracts missing attribute values from both arrays
        const missing = currentAttributeValuesValues.filter(item => compareArrayValuesValues.indexOf(item) < 0);

        if (missing && missing.length > 0) {
            return false;

        }
        return true;
    }

    public SendPriceCheckFromSOFComponent(product: Product) {

        this.runPriceCheckSubject.next(product);
    }

    // If LOI only has 1 value thats a default then add to the list
    public AddProductDefaultLoiToList(_product: Product): number {
        // Get product LOI
        const loiAttr = _product.attributes.find(x => x.name === 'LIMITOFINDEMNITY');
        // check if only 1 LOI attr value exists
        if (loiAttr && loiAttr.values.length === 1) {
            // Take the first attr value
            const loiAttrFirstValue = loiAttr.values[0];
            // if its set as default then add it to the list
            if (loiAttrFirstValue.default) {
                if (this.productDefaultLois.find(x => x.productId === _product.productId) === undefined) {
                    this.productDefaultLois.push(new ProductDefaultLoi(_product.productId, Number(loiAttrFirstValue.value)));
                }
            }
        }

        // return the loi bracket value.
        return _product.loiBracketValue;
    }

    // Gets the default loi from the list using the productid
    public GetProductDefaultLoiValue(productId: number): number {
        // Finds default
        const defaultLoiObj = this.productDefaultLois.find(x => x.productId === productId);
        if (defaultLoiObj) {
            return defaultLoiObj.defaultLoi;
        }
        // If it doesn't exist return 0;
        return 0;
    }
}

